import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import StyledStepper from '../styled/StyledStepper';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { LabelValue } from '@/types/global';
import StyledInput from '../styled/StyledInput';
import SearchIcon from '@/assets/icons/search.svg?react';
import { COUNTRIES } from '../../../../shared/CountriesData';
import StyledSelect from '../styled/StyledSelect';
import {
  useGetDistributorCircuitCountries,
  useGetDistributorCircuitDurations,
  useGetDistributorCircuitReceivers,
  useGetDistributorCircuits,
  useListDistributorEditableCircuits,
} from '@/hooks/useCircuits';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import {
  CIRCUIT_FOR_ESTIMATE_CREATION_COLUMNS,
  EDITABLE_CIRCUIT_FOR_ESTIMATE_CREATION_COLUMNS,
} from '@/types/estimates/circuitsForEstimateCreationColumnDef';
import StyledTable from '../styled/StyledTable';
import { RowSelectionState } from '@tanstack/react-table';
import useTableControls from '@/hooks/useTableControls';
import { PAGE_SIZE } from '@/constants/pageSize';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import EstimateCreationInformationStep from './EstimateCreationInformationStep';
import {
  CreateEstimateFormData,
  CreateEstimateSchema,
} from '@/types/estimates/CreateEstimateFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateEstimate } from '@/hooks/useEstimate';
import { getCircuit } from '@/api/circuits.api';
import { findProject } from '@/api/project.api';
import { queryKeys } from '@/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import Loader from '../ui/Loader';
import { useTranslation } from '@/i18n';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';
import { RadioGroup } from '../ui/radio-group';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';

interface CreateEstimateModalProps {
  closeModal: () => void;
  nbAdults: number;
  nbChildren: number;
  clientId: string;
  projectId: string;
}

type CircuitsFilters = {
  search: string;
  supplier?: LabelValue<string>;
  countryName?: LabelValue<string>;
  duration?: LabelValue<
    { durationDays: number; durationNights: number } | undefined
  >;
};

type CircuitsTab = 'original' | 'editable';

const CreateEstimateModal = ({
  closeModal,
  clientId,
  projectId,
}: CreateEstimateModalProps) => {
  const { t } = useTranslation();

  const ESTIMATE_STEPS = [
    t('estimates.steps.selectCircuit'),
    t('estimates.steps.completeInformation'),
  ];

  const { control: filtersControl } = useForm<CircuitsFilters>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      countryName: undefined,
      supplier: undefined,
      duration: undefined,
    },
  });

  const methods = useForm<CreateEstimateFormData>({
    mode: 'onChange',
    resolver: zodResolver(CreateEstimateSchema),
    defaultValues: {
      marginRate: undefined,
      name: undefined,
      allowAgencyModification: 'false',
      showQuoteName: 'true',
      showSummaryAndImportantPoints: 'true',
      showPriceIncludesExcludes: 'true',
      showModificationCancellationConditions: 'true',
      showDates: 'true',
      allowAgencyInternationalFlights: 'false',
      allowAgencyMarginRate: 'false',
      allowAgencyAdditionalMargin: 'false',
    },
  });
  const { control, handleSubmit } = methods;
  const { currentLanguage } = useLanguage();
  const { isTourOperator } = useDistributorLabels();

  const searchValue = useWatch({ control: filtersControl, name: 'search' });
  const debouncedSearch = useDebouncedSearch(searchValue);
  const countryNameValue = useWatch({
    control: filtersControl,
    name: 'countryName',
  });
  const durationValue = useWatch({ control: filtersControl, name: 'duration' });

  const [currStep, setCurrStep] = useState(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const modalActionTitle = useMemo(
    () =>
      currStep === 0
        ? t('estimates.selectCircuit')
        : t('estimates.createEstimate'),
    [currStep, t],
  );

  const { page, setPage, sortField, sortOrder, sorting, setSorting } =
    useTableControls();

  const filters = useMemo(
    () => ({
      name: debouncedSearch,
      countries: countryNameValue?.value ?? '',
      durationDays: durationValue?.value?.durationDays.toString() ?? '',
      durationNights: durationValue?.value?.durationNights?.toString() ?? '',
    }),
    [
      countryNameValue?.value,
      debouncedSearch,
      durationValue?.value?.durationDays,
      durationValue?.value?.durationNights,
    ],
  );

  const [currTab, setCurrTab] = useState<CircuitsTab>('original');
  const onRadioChange = (value: string) => {
    setRowSelection(value ? { [value]: true } : {});
  };

  const {
    data: circuitResponse,
    isSuccess,
    isLoading,
  } = useGetDistributorCircuits({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const {
    data: editableCircuitsResponse,
    isSuccess: isEditableCircuitsSuccess,
    isLoading: isEditableCircuitsLoading,
  } = useListDistributorEditableCircuits(
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
    },
    currTab === 'editable',
  );

  const { data: receiverOptions } = useGetDistributorCircuitReceivers({
    select: (data) => data.map(({ id, name }) => ({ value: id, label: name })),
  });

  const { data: countryOptions } = useGetDistributorCircuitCountries({
    select: (data) =>
      data?.map(({ country }) => ({ value: country, label: country })),
  });

  const { data: durationOptions } = useGetDistributorCircuitDurations({
    select: (data) =>
      data?.map(({ durationDays, durationNights }) => ({
        value: { durationDays, durationNights },
        label: durationNights
          ? `${durationDays}j / ${durationNights}n`
          : `${durationDays}j`,
      })),
  });

  const selectedCircuitId = useMemo(
    () =>
      Object.keys(rowSelection).length > 0
        ? Object.keys(rowSelection)[0]
        : null,
    [rowSelection],
  );

  const watchedAdults = useWatch({
    control,
    name: 'adults',
    disabled: currStep === 0,
  });
  const watchedChildren = useWatch({
    control,
    name: 'children',
    disabled: currStep === 0,
  });

  const canCreateEstimate = useMemo(() => {
    const adults = watchedAdults ?? [];
    const children = watchedChildren ?? [];
    const hasSelectedAdult = adults.some(({ isSelected }) => isSelected);
    const childrenAreValid = children.every(
      ({ birthDate }) => birthDate != undefined,
    );
    return hasSelectedAdult && childrenAreValid;
  }, [watchedAdults, watchedChildren]);

  const isActionDisabled =
    currStep === 0 ? !selectedCircuitId : !canCreateEstimate;

  const { mutateAsync: createEstimate } = useCreateEstimate(
    projectId,
    currTab === 'editable',
  );
  const { toastAsync } = useToast();
  const queryClient = useQueryClient();

  const estimateCreationCircuitOptions = useMemo(
    () =>
      ({
        signImages: 'mainOnly',
        skipTranslations: true,
        pricingPreview: true,
      }) as const,
    [],
  );

  useEffect(() => {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.projects.detail(clientId, projectId),
      queryFn: () => findProject(clientId, projectId),
    });
  }, [clientId, projectId, queryClient]);

  useEffect(() => {
    if (!selectedCircuitId || currTab !== 'original') return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.circuits.detail(selectedCircuitId, {
        originalImage: false,
        shouldConvertToEuro: true,
        ...estimateCreationCircuitOptions,
      }),
      queryFn: () =>
        getCircuit(
          selectedCircuitId,
          false,
          true,
          estimateCreationCircuitOptions,
        ),
    });
  }, [currTab, estimateCreationCircuitOptions, queryClient, selectedCircuitId]);

  const selectedOriginalCircuitId = useMemo(() => {
    if (currTab === 'original') return selectedCircuitId;
    return (
      editableCircuitsResponse?.circuits.find(
        (circuit) => circuit.id === selectedCircuitId,
      )?.originalCircuit.id ?? null
    );
  }, [currTab, editableCircuitsResponse?.circuits, selectedCircuitId]);

  const submitForm: SubmitHandler<CreateEstimateFormData> = useCallback(
    async (data) => {
      if (selectedCircuitId == null) return;
      const { status } = await toastAsync(
        createEstimate({
          language: currentLanguage,
          marginRate: data.marginRate,
          name: data.name,
          circuitId: selectedOriginalCircuitId ?? selectedCircuitId,
          adults: data.adults
            .filter(({ isSelected }) => isSelected)
            .map(({ firstName, lastName }) => ({
              firstName,
              lastName,
            })),
          children: data.children
            .filter(({ isSelected }) => isSelected)
            .map(({ firstName, lastName, birthDate }) => ({
              firstName,
              lastName,
              birthDate,
            })),
          editableCircuitId:
            currTab === 'editable' ? selectedCircuitId : undefined,
          ...(isTourOperator
            ? {
                allowAgencyModification:
                  data.allowAgencyModification === 'true',
                showQuoteName: data.showQuoteName === 'true',
                showSummaryAndImportantPoints:
                  data.showSummaryAndImportantPoints === 'true',
                showPriceIncludesExcludes:
                  data.showPriceIncludesExcludes === 'true',
                showModificationCancellationConditions:
                  data.showModificationCancellationConditions === 'true',
                showDates: data.showDates === 'true',
                allowAgencyInternationalFlights:
                  data.allowAgencyInternationalFlights === 'true',
                allowAgencyMarginRate: data.allowAgencyMarginRate === 'true',
                allowAgencyAdditionalMargin:
                  data.allowAgencyAdditionalMargin === 'true',
              }
            : {}),
        }),
        { success: t('estimates.creation.success') },
      );
      if (status >= 200 && status < 300) closeModal();
    },
    [
      currentLanguage,
      selectedCircuitId,
      toastAsync,
      createEstimate,
      selectedOriginalCircuitId,
      currTab,
      isTourOperator,
      t,
      closeModal,
    ],
  );

  const modalActionOnClick = useCallback(() => {
    if (currStep === 0) setCurrStep(1);
    else handleSubmit(submitForm)();
  }, [currStep, handleSubmit, submitForm]);

  return (
    <Modal
      title={t('modal.createEstimate.title')}
      closeModal={closeModal}
      actionTitle={modalActionTitle}
      actionOnClick={modalActionOnClick}
      shouldCloseModalAfterAction={false}
      isActionDisabled={isActionDisabled}
      containerClassName="max-w-3xl w-full"
    >
      <StyledStepper
        activeStep={currStep}
        steps={ESTIMATE_STEPS}
        setStep={setCurrStep}
        disableNext={!selectedCircuitId}
      />
      {currStep === 0 && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col w-full gap-2">
            <span className="text-xs font-normal text-gray-600">
              {t('forms.labels.filterBy')}
            </span>
            <div className="flex flex-col w-full gap-4">
              <StyledInput
                control={filtersControl}
                name="search"
                placeholder={t('forms.placeholders.quickSearch')}
                icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
              />
              <div className="flex flex-col lg:flex-row w-full gap-4">
                <StyledSelect
                  control={filtersControl}
                  options={countryOptions ?? COUNTRIES}
                  name="countryName"
                  placeholder={t('forms.labels.country')}
                  isClearable
                />
                <StyledSelect
                  control={filtersControl}
                  options={durationOptions ?? []}
                  name="duration"
                  placeholder={t('estimates.selectDuration')}
                  isClearable
                />
                <StyledSelect
                  control={filtersControl}
                  options={receiverOptions ?? []}
                  name="supplier"
                  placeholder={t('estimates.selectSupplierFilter')}
                  isClearable
                />
              </div>
            </div>
          </div>
          {isLoading || isEditableCircuitsLoading ? (
            <Loader />
          ) : (
            <Tabs
              value={currTab}
              onValueChange={(value) => setCurrTab(value as CircuitsTab)}
            >
              <TabsList>
                <TabsTrigger value="original" className="hover:cursor-pointer">
                  {t('estimates.myCatalogue')}
                </TabsTrigger>
                <TabsTrigger value="editable" className="hover:cursor-pointer">
                  {t('estimates.editableCircuits')}
                </TabsTrigger>
              </TabsList>
              <div className="flex flex-col gap-2">
                <div className="max-h-[492px] overflow-y-auto">
                  <TabsContent value="original">
                    {isSuccess && (
                      <>
                        <RadioGroup
                          value={selectedCircuitId ?? ''}
                          onValueChange={onRadioChange}
                        >
                          <StyledTable
                            columns={CIRCUIT_FOR_ESTIMATE_CREATION_COLUMNS(t)}
                            data={circuitResponse.circuits}
                            getRowId={(row) => row.circuit.id}
                            setSelection={setRowSelection}
                            rowSelection={rowSelection}
                            sorting={sorting}
                            setSorting={setSorting}
                          />
                        </RadioGroup>
                        <StyledNextPreviousButtons
                          page={page}
                          pageSize={PAGE_SIZE}
                          setPage={setPage}
                          dataLength={circuitResponse.total}
                          label="circuit"
                        />
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="editable">
                    {isEditableCircuitsSuccess && (
                      <>
                        <RadioGroup
                          value={selectedCircuitId ?? ''}
                          onValueChange={onRadioChange}
                        >
                          <StyledTable
                            columns={EDITABLE_CIRCUIT_FOR_ESTIMATE_CREATION_COLUMNS(
                              t,
                            )}
                            data={editableCircuitsResponse?.circuits ?? []}
                            getRowId={(row) => row.id}
                            setSelection={setRowSelection}
                            rowSelection={rowSelection}
                            sorting={sorting}
                            setSorting={setSorting}
                          />
                        </RadioGroup>
                        <StyledNextPreviousButtons
                          page={page}
                          pageSize={PAGE_SIZE}
                          setPage={setPage}
                          dataLength={editableCircuitsResponse.total}
                          label="circuit"
                        />
                      </>
                    )}
                  </TabsContent>
                </div>
              </div>
            </Tabs>
          )}
        </div>
      )}
      {currStep === 1 && selectedCircuitId && (
        <FormProvider {...methods}>
          <EstimateCreationInformationStep
            circuitId={selectedCircuitId}
            projectId={projectId}
            clientId={clientId}
            control={control}
            fromEditableCircuit={currTab === 'editable'}
          />
        </FormProvider>
      )}
    </Modal>
  );
};

export default CreateEstimateModal;
